import { Injectable, inject } from '@angular/core';
import { UriBuilder } from 'uribuilder';
import { catchError, map, Observable, of } from 'rxjs';
import {
    FileResponse,
    HttpCacheKeyPrefix,
    HttpExtras,
    IFileResponse,
    NotificationMessageLevel,
    NotificationService,
} from '@lib/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { finalize, switchMap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { EnvService } from '@lib/env';

const HTTP_STATUS_PAYLOAD_TOO_LARGE = 413; // unfortunately the enum does not contain this entry

@Injectable({
    providedIn: 'root',
})
export class FileDataService {
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvService);
    private readonly notificationService = inject(NotificationService);


    /**
     * Adds a File to a document or one of its items.
     * @param formData
     * @param documentId - The document to add the file to
     * @param internal - if the file is for internal use only. Will not be transferred to the supplier after approvement
     * @param itemId - (optional) if present it adds the file to the item, instead of the main document
     */
    public addFile(
        formData: FormData,
        documentId: string,
        internal: boolean,
        itemId?: string,
    ): Observable<FileResponse[]> {
        this.notificationService.spinner.show();
        const cacheKey = itemId ? HttpCacheKeyPrefix.DOCUMENT_ITEM_FILES : HttpCacheKeyPrefix.DOCUMENT_FILES;

        const options: HttpExtras = new HttpExtras({
            pruneCacheWithPrefix: cacheKey,
            customErrorhandling: true,
            errorMessage: {
                [HTTP_STATUS_PAYLOAD_TOO_LARGE]: marker('Die Datei überschreitet die maximale Größe von 10mb.'),
            },
        });

        const uri = this.getFileUri(documentId, itemId);
        uri.query = {
            internal,
        };

        return this.http
            .post<void>(uri.toString(), formData, {
                params: options,
            })
            .pipe(
                switchMap(() => this.getFiles(documentId, itemId)),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === HTTP_STATUS_PAYLOAD_TOO_LARGE) {
                        const message = marker('Die Datei überschreitet die maximale Größe von 10mb.');
                        this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                    } else if (error.status === HttpStatusCode.BadRequest && typeof error.error === 'string') {
                        this.notificationService.showNotification(error.error, NotificationMessageLevel.ERROR);
                    } else {
                        const message = marker('Beim Hochladen der Datei ist leider ein Fehler aufgetreten');
                        this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                    }

                    return of();
                }),
                finalize(() => this.notificationService.spinner.hide()),
            );
    }

    public deleteFile(fileId: string, documentId: string, itemId?: string): Observable<FileResponse[]> {
        const cacheKey = itemId ? HttpCacheKeyPrefix.DOCUMENT_ITEM_FILES : HttpCacheKeyPrefix.DOCUMENT_FILES;

        const uri = this.getFileUri(documentId, itemId);
        uri.pathSegments.push(fileId);

        return this.http
            .delete<void>(uri.toString(), {
                params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(cacheKey),
            })
            .pipe(switchMap(() => this.getFiles(documentId, itemId)));
    }

    /**
     * Get Files for an item or the document itself
     * @param documentId
     * @param itemId
     */
    public getFiles(documentId: string, itemId?: string): Observable<FileResponse[]> {
        const cacheKey = itemId ? HttpCacheKeyPrefix.DOCUMENT_ITEM_FILES : HttpCacheKeyPrefix.DOCUMENT_FILES;
        return this.http
            .get<IFileResponse[]>(this.getFileUri(documentId, itemId).toString(), {
                params: HttpExtras.CACHE_KEY_PREFIX(cacheKey),
            })
            .pipe(map((files) => files.map((f) => new FileResponse(f))));
    }

    private getFileUri(documentId: string, itemId?: string): UriBuilder {
        return itemId
            ? this.getBase(documentId, 'item', itemId, 'extensions', 'files')
            : this.getBase(documentId, 'extensions', 'files');
    }

    private getBase(...pathSegments: (string | number)[]): UriBuilder {
        return this.env.getBase('document', ...pathSegments);
    }
}
